export enum CSV_UploadStatus {
  Pending = 'PENDING',
  Success = 'SUCCESS',
  Failed = 'FAILED',
}

/**
 * Represents a parsed facility row from the CSV file
 * lat and lon are optional - if not provided, they will be geocoded from the address
 * timezone is optional - if not provided, a default will be used
 */
export interface ParsedFacilityI {
  name: string
  shortName: string
  address: string
  lat?: string
  lon?: string
  timezone?: string
  uploadStatus?: CSV_UploadStatus
  uploadError?: string
  geocodingError?: string
}

/**
 * Result of CSV parsing operation
 */
export interface CSVParseResultI {
  data: ParsedFacilityI[]
  errors: string[]
}

const MAX_FACILITIES_PER_CSV = 100
const LINE_BREAK_REGEX = /\r?\n/
// Required CSV headers (lat, lon, and timezone are optional)
const REQUIRED_CSV_HEADERS = ['name', 'shortname', 'address']

/**
 * Parses a CSV line handling quoted fields (for addresses with commas)
 * @param line - CSV line to parse
 * @returns Array of field values
 */
const parseCSVLine = (line: string): string[] => {
  const fields: string[] = []
  let currentField = ''
  let isInsideQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    const nextChar = line[i + 1]

    if (char === '"') {
      // Handle escaped quotes ("")
      if (isInsideQuotes && nextChar === '"') {
        currentField += '"'
        i++ // Skip next quote
      } else {
        // Toggle quote state
        isInsideQuotes = !isInsideQuotes
      }
    } else if (char === ',' && !isInsideQuotes) {
      // End of field
      fields.push(currentField.trim())
      currentField = ''
    } else {
      currentField += char
    }
  }

  // Add the last field
  fields.push(currentField.trim())

  return fields
}

/**
 * Checks if an optional field is provided but not a valid number
 * @param value - The value to check
 * @returns true if the value is provided but not a valid number, false otherwise
 */
const isInvalidOptionalNumber = (value?: string): boolean => {
  return !!(value && value.trim() !== '' && isNaN(parseFloat(value)))
}

/**
 * Validates a parsed facility row
 * @param facility - The facility object to validate
 * @param rowNumber - Row number for error reporting
 * @returns Array of validation errors (empty if valid)
 */
const validateFacility = (
  facility: Partial<ParsedFacilityI>,
  rowNumber: number
): string[] => {
  const errors: string[] = []

  if (!facility.name || facility.name.trim() === '') {
    errors.push(`Row ${rowNumber}: Missing required field 'name'`)
  }

  if (!facility.shortName || facility.shortName.trim() === '') {
    errors.push(`Row ${rowNumber}: Missing required field 'shortName'`)
  }

  if (!facility.address || facility.address.trim() === '') {
    errors.push(`Row ${rowNumber}: Missing required field 'address'`)
  }

  // lat and lon are optional - if not provided, they will be geocoded from address
  // But if provided, they must be valid numbers
  if (isInvalidOptionalNumber(facility.lat)) {
    errors.push(`Row ${rowNumber}: 'lat' must be a valid number if provided`)
  }

  if (isInvalidOptionalNumber(facility.lon)) {
    errors.push(`Row ${rowNumber}: 'lon' must be a valid number if provided`)
  }

  // timezone is optional - no validation needed

  return errors
}

/**
 * Parses a CSV file containing facility data
 * Expected columns: name, shortName, address, lat, lon, timezone
 * Note: lat, lon, and timezone are optional
 *   - lat/lon will be geocoded from the address if not provided
 *   - timezone will be automatically determined from coordinates
 * Addresses may contain commas and should be wrapped in quotes
 *
 * @param file - The CSV file to parse
 * @returns Promise resolving to parsed facilities and any errors
 *
 * @example
 * ```csv
 * name,shortName,address,lat,lon,timezone
 * "Main Office","HQ","123 Main St, Suite 100",40.7128,-74.0060,"America/New_York"
 * "Branch Office","BR","456 Oak Ave",,,
 * "With Coords","WC","789 Pine St",34.0522,-118.2437,
 * ```
 */
export const parseCSVFile = async (file: File): Promise<CSVParseResultI> => {
  return new Promise((resolve) => {
    const reader = new FileReader()

    reader.onload = (event) => {
      const csvText = event.target?.result as string
      const lines = csvText
        .split(LINE_BREAK_REGEX)
        .filter((line) => line.trim() !== '')

      if (lines.length === 0) {
        return resolve({
          data: [],
          errors: ['CSV file is empty'],
        })
      }

      if (lines.length > MAX_FACILITIES_PER_CSV + 1) {
        return resolve({
          data: [],
          errors: [
            `CSV file cannot have more than ${MAX_FACILITIES_PER_CSV} facilities.`,
          ],
        })
      }

      // Parse header
      const headerLine = lines[0]
      const headers = parseCSVLine(headerLine).map((h) => h.toLowerCase())

      // Validate required headers only
      const missingHeaders = REQUIRED_CSV_HEADERS.filter(
        (h) => !headers.includes(h)
      )

      if (missingHeaders.length > 0) {
        return resolve({
          data: [],
          errors: [
            `Missing required columns: ${missingHeaders.join(
              ', '
            )}. Required columns: name, shortName, address. Optional columns: lat, lon, timezone`,
          ],
        })
      }

      // Find column indices
      const nameIndex = headers.indexOf('name')
      const shortNameIndex = headers.indexOf('shortname')
      const addressIndex = headers.indexOf('address')
      const latIndex = headers.indexOf('lat')
      const lonIndex = headers.indexOf('lon')
      const timezoneIndex = headers.indexOf('timezone')

      const facilities: ParsedFacilityI[] = []
      const errors: string[] = []

      // Parse data rows
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim()
        if (line === '') continue

        const fields = parseCSVLine(line)
        const rowNumber = i + 1

        const facility: Partial<ParsedFacilityI> = {
          name: fields[nameIndex] || '',
          shortName: fields[shortNameIndex] || '',
          address: fields[addressIndex] || '',
          // Only include lat/lon if columns exist in CSV
          lat: latIndex >= 0 ? fields[latIndex] || '' : undefined,
          lon: lonIndex >= 0 ? fields[lonIndex] || '' : undefined,
          // Only include timezone if column exists in CSV
          timezone:
            timezoneIndex >= 0 ? fields[timezoneIndex] || '' : undefined,
        }

        // Validate the facility
        const validationErrors = validateFacility(facility, rowNumber)
        if (validationErrors.length > 0) {
          errors.push(...validationErrors)
        } else {
          facilities.push(facility as ParsedFacilityI)
        }
      }

      resolve({
        data: facilities,
        errors,
      })
    }

    reader.onerror = () => {
      resolve({
        data: [],
        errors: ['Failed to read CSV file'],
      })
    }

    reader.readAsText(file)
  })
}

/**
 * Downloads a CSV template file with the correct headers for bulk facility upload
 * Includes examples showing that lat, lon, and timezone can be left empty
 * - Coordinates will be geocoded from address
 * - Timezone will be determined from coordinates
 */
export const downloadCSVTemplate = () => {
  const headers = ['name', 'shortName', 'address', 'lat', 'lon', 'timezone']
  const exampleRow1 = [
    'Full Details Facility',
    'FDF',
    '"123 Main Street, City, State"', // Quoted because it contains commas
    40.7128,
    -74.006,
    'America/New_York',
  ]
  const exampleRow2 = [
    'Auto-Geocoded Facility',
    'AGF',
    '"Carlow, Ireland"',
    '', // Empty lat - will be geocoded
    '', // Empty lon - will be geocoded
    '', // Empty timezone - will be determined from coordinates
  ]
  const exampleRow3 = [
    'Coordinates Without Timezone',
    'CWT',
    '"789 Pine Road, City, State"',
    34.0522, // Coordinates provided
    -118.2437,
    '', // Empty timezone - will be determined from coordinates
  ]

  const csvContent = [
    headers.join(','),
    exampleRow1.join(','),
    exampleRow2.join(','),
    exampleRow3.join(','),
    '', // Empty row for user to fill
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', 'facilities_template.csv')
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
